import { FC, useEffect, useState } from 'react';
import { Box, Button, Stack, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DrawerComponent from '../../../../../../components/Drawer/Drawer';
import {
  Timeline,
  TimelineItem,
  TimelineBody,
} from '../../../../../../components/Timeline/Timeline';
import { Deal } from '@api/deals';
import DealSidePanelItem from './DealSidePanelItem';
import { formatTimestamp } from '@utils/common';
import { createFormattedPercentValue } from '@utils/percentageInputsUtils';

// Constants
const PRICING_METHODS_MAP: Record<string, string> = {
  SIMPLE_INTEREST: 'Simple Interest',
  DYNAMIC_RETURN_CAP: 'Dynamic Return Cap',
  CUSTOM: 'Custom Return Cap',
};

// Types
interface DealSetupSidePanelProps {
  showDealSetupPanel: boolean;
  setShowDealSetupPanel: React.Dispatch<React.SetStateAction<boolean>>;
  deals: Deal[];
}

const styles = {
  container: {
    padding: '1.5rem',
    pr: '0',
    mb: '2rem',
    width: 'min(95vw, 600px)',
    height: '100%',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1.375rem',
    fontWeight: 700,
    lineHeight: '1.875rem',
    color: '#6C6C6C',
    marginBottom: '1.875rem',
    flex: 0,
    pr: '1.5rem',
  },
  timelineContainer: {
    flex: 1,
    overflowY: 'scroll',
    px: '1.5rem',
  },
  infoCard: (isMobile: boolean) => ({
    backgroundColor: '#2F736E1F',
    borderRadius: '0.75rem',
    p: '0.75rem',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: '1rem',
  }),
  detailsCard: (isMobile: boolean) => ({
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    padding: '1rem',
    background: '#fff',
    borderRadius: '0.75rem',
    gap: '1rem',
  }),
  textContent: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  overflowEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  closeButton: {
    padding: 0,
    minWidth: 'fit-content',
  },
  closeIcon: {
    color: '#162C36',
  },
};

// Helper components
const CreatorInfoCard: FC<{
  deal: Deal;
  isInitialDeal: boolean;
  isMobile: boolean;
}> = ({ deal, isInitialDeal, isMobile }) => (
  <Box sx={styles.infoCard(isMobile)}>
    <DealSidePanelItem
      label={isInitialDeal ? 'Created By' : 'Updated By'}
      value={deal.createdByEmail}
      bold
      sx={styles.textContent}
    />
    <DealSidePanelItem
      label={isInitialDeal ? 'Created At' : 'Updated At'}
      value={formatTimestamp(deal.createdAt)}
      bold
      sx={styles.textContent}
    />
  </Box>
);

const StatusCard: FC<{ deal: Deal; isMobile: boolean }> = ({ deal, isMobile }) => {
  return (
    <Box sx={styles.detailsCard(isMobile)}>
      <DealSidePanelItem label="Game ID" value={deal.gameId || '-'} sx={styles.textContent} />

      <DealSidePanelItem label="Status" value={deal.status || '-'} sx={styles.textContent} />
    </Box>
  );
};

const PricingCard: FC<{ deal: Deal; isMobile: boolean }> = ({ deal, isMobile }) => {
  if (!deal.pricingMethodology) return null;

  const renderPricingDetails = () => {
    if (!deal.interest) return null;

    switch (deal.pricingMethodology) {
      case 'SIMPLE_INTEREST': {
        const interestRate = parseFloat(JSON.parse(deal.interest)['SIMPLE_INTEREST_RATE']) * 100;
        return (
          <DealSidePanelItem
            label="Interest"
            value={createFormattedPercentValue(interestRate)}
            sx={styles.textContent}
          />
        );
      }
      case 'DYNAMIC_RETURN_CAP': {
        const parsedInterest = JSON.parse(deal.interest);
        const baseRate = parseFloat(parsedInterest['BASE_RATE']) * 100;
        const dynamicRate = parseFloat(parsedInterest['DYNAMIC_RATE']) * 100;

        return (
          <>
            <DealSidePanelItem
              label="First Month Cap"
              value={createFormattedPercentValue(baseRate)}
              sx={styles.textContent}
            />
            <DealSidePanelItem
              label="Incremental Return Cap"
              value={createFormattedPercentValue(dynamicRate)}
              sx={styles.textContent}
            />
          </>
        );
      }
      case 'CUSTOM':
        return (
          <DealSidePanelItem label="Custom Pricing" value={deal.interest} sx={styles.textContent} />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        ...styles.detailsCard(isMobile),
        ...styles.overflowEllipsis,
      }}
    >
      <DealSidePanelItem
        label="Pricing Methodology"
        value={PRICING_METHODS_MAP[deal.pricingMethodology]}
        sx={styles.textContent}
      />
      {renderPricingDetails()}
    </Box>
  );
};

const DateCard: FC<{ deal: Deal; isMobile: boolean }> = ({ deal, isMobile }) => {
  return (
    <Box sx={styles.detailsCard(isMobile)}>
      <DealSidePanelItem label="Start Date" value={deal.startDate || '-'} sx={styles.textContent} />
      <DealSidePanelItem label="End Date" value={deal.endDate || '-'} sx={styles.textContent} />
    </Box>
  );
};

const CohortCard: FC<{ deal: Deal; isMobile: boolean }> = ({ deal, isMobile }) => {
  if (deal.underspentInterest === null && deal.cohortMonth === null) return null;

  return (
    <Box sx={styles.detailsCard(isMobile)}>
      {deal.underspentInterest !== null && (
        <DealSidePanelItem
          label="Interest on Underspent Amount"
          value={createFormattedPercentValue(deal.underspentInterest * 100)}
          sx={styles.textContent}
        />
      )}
      {deal.cohortMonth !== null && (
        <DealSidePanelItem
          label="Cohort Month Offset"
          value={deal.cohortMonth.toString()}
          sx={styles.textContent}
        />
      )}
    </Box>
  );
};

const InvestorSplitCards: FC<{ deal: Deal; isMobile: boolean }> = ({ deal, isMobile }) => {
  if (!deal.investorContributionSplits?.length) return null;

  return (
    <>
      {deal.investorContributionSplits.map((split, index) => (
        <Box key={index} sx={styles.detailsCard(isMobile)}>
          <DealSidePanelItem label="Investor" value={split.investor} sx={styles.textContent} />
          <DealSidePanelItem
            label="Contribution Percentage"
            value={createFormattedPercentValue(split.contributionPercentage * 100)}
            sx={styles.textContent}
          />
          <DealSidePanelItem
            label="Investor Return Cap"
            value={createFormattedPercentValue(split.returnCap * 100)}
            sx={styles.textContent}
          />
        </Box>
      ))}
    </>
  );
};

const DealTimelineItem: FC<{
  deal: Deal;
  index: number;
  totalDeals: number;
  isMobile: boolean;
}> = ({ deal, index, totalDeals, isMobile }) => {
  const isInitialDeal = index === totalDeals - 1;

  return (
    <TimelineItem>
      <TimelineBody>
        <Stack direction="column" gap="1rem">
          <CreatorInfoCard deal={deal} isInitialDeal={isInitialDeal} isMobile={isMobile} />
          <StatusCard deal={deal} isMobile={isMobile} />
          <PricingCard deal={deal} isMobile={isMobile} />
          <DateCard deal={deal} isMobile={isMobile} />
          <CohortCard deal={deal} isMobile={isMobile} />
          <InvestorSplitCards deal={deal} isMobile={isMobile} />
        </Stack>
      </TimelineBody>
    </TimelineItem>
  );
};

// Main component
const DealSetupSidePanel: FC<DealSetupSidePanelProps> = ({
  showDealSetupPanel,
  setShowDealSetupPanel,
  deals,
}) => {
  const [logs, setLogs] = useState<Deal[]>([]);

  useEffect(() => {
    setLogs(diff(deals));
  }, [deals]);

  const diff = (deals: Deal[]): Deal[] => {
    if (deals.length === 0) return [];

    const copy: Deal[] = [...deals];
    const newArr: Deal[] = [copy[copy.length - 1]];

    for (let i = copy.length - 2; i >= 0; i--) {
      const prev = { ...copy[i + 1] };
      const current: any = { ...copy[i] };

      if (prev.pricingMethodology === current.pricingMethodology) {
        if (prev.pricingMethodology === 'SIMPLE_INTEREST') {
          if (
            JSON.parse(prev.interest)['SIMPLE_INTEREST_RATE'] ===
            JSON.parse(current.interest)['SIMPLE_INTEREST_RATE']
          ) {
            current.interest = null;
            current.pricingMethodology = null;
          }
        } else if (prev.pricingMethodology === 'DYNAMIC_RETURN_CAP') {
          if (
            JSON.parse(prev.interest)['BASE_RATE'] === JSON.parse(current.interest)['BASE_RATE'] &&
            JSON.parse(prev.interest)['DYNAMIC_RATE'] ===
              JSON.parse(current.interest)['DYNAMIC_RATE']
          ) {
            current.interest = null;
            current.pricingMethodology = null;
          }
        } else if (prev.pricingMethodology === 'CUSTOM') {
          if (prev.interest === current.interest) {
            current.interest = null;
            current.pricingMethodology = null;
          }
        }
      }

      if (prev.cohortMonth === current.cohortMonth) {
        current.cohortMonth = null;
      }

      if (prev.underspentInterest === current.underspentInterest) {
        current.underspentInterest = null;
      }

      if (prev.investorContributionSplits.length === current.investorContributionSplits.length) {
        // Lengths are equal, there is a possiblity that the splits are equal
        let identical = true;
        for (let j = 0; j < prev.investorContributionSplits.length; j++) {
          // Loop through the investor contribution splits of the current and if the investor equals the current, check if the contribution percentage and return cap are equal
          // If not, set identical to false and break
          // If no match for investor is found in one iter, then set identical to false and break
          let itemFound = false;

          for (let k = 0; k < current.investorContributionSplits.length; k++) {
            if (
              prev.investorContributionSplits[j].investor ===
              current.investorContributionSplits[k].investor
            ) {
              itemFound = true;
              if (
                prev.investorContributionSplits[j].contributionPercentage !==
                  current.investorContributionSplits[k].contributionPercentage ||
                prev.investorContributionSplits[j].returnCap !==
                  current.investorContributionSplits[k].returnCap
              ) {
                identical = false;
                break;
              }
            }
          }

          if (!itemFound) {
            identical = false;
            break;
          }

          if (!identical) {
            break;
          }
        }

        if (identical) {
          current.investorContributionSplits = null;
        }
      }

      newArr.push(current);
    }

    return newArr.reverse();
  };

  const handleClose = () => setShowDealSetupPanel(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <DrawerComponent
      open={showDealSetupPanel}
      anchor="right"
      close={handleClose}
      height="98vh"
      zIndex={9000}
      backgroundColor="#F0F0F1"
    >
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          Updates
          <Button sx={styles.closeButton} onClick={handleClose}>
            <CloseIcon sx={styles.closeIcon} />
          </Button>
        </Box>

        <Box sx={styles.timelineContainer}>
          <Timeline>
            {logs.map((deal, index) => (
              <DealTimelineItem
                key={index}
                deal={deal}
                index={index}
                totalDeals={deals.length}
                isMobile={isMobile}
              />
            ))}
          </Timeline>
        </Box>
      </Box>
    </DrawerComponent>
  );
};

export default DealSetupSidePanel;
